import React, { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import {
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import axios from "../utils/requests";

const RECENT_ORGS_KEY = 'unionly_recent_org_ids';
const MAX_RECENT_ORGS = 10;
const RECENT_DISPLAY_LIMIT = 8;

function getRecentOrgIds() {
  try {
    const stored = localStorage.getItem(RECENT_ORGS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveRecentOrgId(orgId) {
  const ids = getRecentOrgIds();
  const updated = [orgId, ...ids.filter(id => id !== orgId)].slice(0, MAX_RECENT_ORGS);
  localStorage.setItem(RECENT_ORGS_KEY, JSON.stringify(updated));
}

function buildIdsQuery(ids) {
  return ids.map(id => `ids[]=${encodeURIComponent(id)}`).join('&');
}

// Main OrgSwitcher Component
const OrgSwitcher = ({ current_organization, organizations }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [recentOrgs, setRecentOrgs] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);

  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const recentIds = getRecentOrgIds();
    if (recentIds.length === 0) {
      setRecentOrgs(organizations.slice(0, RECENT_DISPLAY_LIMIT));
      return;
    }

    let cancelled = false;
    setIsLoadingRecent(true);

    axios.get(`/dashboard/organizations?${buildIdsQuery(recentIds)}`)
      .then(response => {
        if (cancelled) return;
        const byId = Object.fromEntries(response.data.map(o => [o.id, o]));
        const ordered = recentIds.map(id => byId[id]).filter(Boolean);
        setRecentOrgs(ordered.slice(0, RECENT_DISPLAY_LIMIT));
      })
      .catch(() => {
        if (cancelled) return;
        setRecentOrgs(organizations.slice(0, RECENT_DISPLAY_LIMIT));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRecent(false);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  // Organizations to display (search results or recent)
  const displayOrgs = searchQuery.length >= 2 ? searchResults : recentOrgs;
  const filteredOrgs = displayOrgs.filter(org => org.id !== current_organization.id);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Keyboard shortcut to open (Cmd+K or Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeModal]);

  // Search with debounce
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(`/dashboard/organizations?search_value=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOrgs.length]);

  // Scroll highlighted item into view (smooth, only when needed)
  useEffect(() => {
    if (!isOpen || filteredOrgs.length === 0) return;

    const item = listRef.current?.querySelector(`[data-org-index="${highlightedIndex}"]`);
    if (item) {
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [highlightedIndex, isOpen, filteredOrgs.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, filteredOrgs.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOrgs[highlightedIndex]) {
          switchToOrg(filteredOrgs[highlightedIndex]);
        }
        break;
    }
  }, [isOpen, filteredOrgs, highlightedIndex]);

  const switchToOrg = (org) => {
    saveRecentOrgId(org.id);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `/dashboard/${org.id}/update_org`;

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const getInitials = (name) => {
    return (name || '')
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getOrgGradient = (name) => {
    const gradients = [
      'from-indigo-500 to-violet-600',
      'from-blue-500 to-cyan-500',
      'from-emerald-500 to-teal-600',
      'from-amber-500 to-orange-500',
      'from-rose-500 to-pink-600',
      'from-purple-500 to-fuchsia-600',
      'from-sky-500 to-blue-600',
      'from-orange-400 to-amber-500',
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  };

  // Single org - just show name
  if (organizations.length <= 1) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${getOrgGradient(current_organization.name)} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <span className="text-xs font-bold text-white">{getInitials(current_organization.name)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate" title={current_organization.name}>
              {current_organization.name}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50/80 transition-colors group rounded-lg"
      >
        <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${getOrgGradient(current_organization.name)} flex items-center justify-center flex-shrink-0 shadow-sm`}>
          <span className="text-xs font-bold text-white">{getInitials(current_organization.name)}</span>
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium text-gray-900 truncate" title={current_organization.name}>
            {current_organization.name}
          </p>
          <p className="text-xs text-gray-500">Switch organization</p>
        </div>
        <ChevronUpDownIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
      </button>

      {/* Modal */}
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel
                  className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200/50 transition-all"
                  onKeyDown={handleKeyDown}
                >
                  {/* Header - subtle gradient */}
                  <div className="bg-gradient-to-br from-slate-50 to-white px-6 py-5 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                          <BuildingOffice2Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <DialogTitle className="text-lg font-semibold text-slate-900 tracking-tight">
                            Switch Organization
                          </DialogTitle>
                          <p className="text-sm text-slate-500 mt-0.5">
                            Select which organization to view
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={closeModal}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="px-5 py-4 border-b border-slate-100">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search organizations..."
                        className="w-full pl-10 pr-10 py-2.5 text-sm bg-slate-50/80 border-0 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:bg-white placeholder:text-slate-400 transition-colors"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200/50 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                        <kbd className="px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-500 font-medium">↑↓</kbd>
                        Navigate
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                        <kbd className="px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-500 font-medium">↵</kbd>
                        Select
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                        <kbd className="px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-500 font-medium">⌘K</kbd>
                        Open
                      </span>
                    </div>
                  </div>

                  {/* Current Organization */}
                  <div className="px-5 py-4">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2.5">Current</p>
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-indigo-50/80 to-violet-50/50 ring-1 ring-indigo-100/80">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getOrgGradient(current_organization.name)} flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-white`}>
                        <span className="text-sm font-bold text-white">{getInitials(current_organization.name)}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 break-words">
                          {current_organization.name}
                        </p>
                        <p className="text-xs text-indigo-600/90 font-medium mt-0.5">Currently viewing</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckIcon className="w-4 h-4 text-indigo-600" />
                      </div>
                    </div>
                  </div>

                  {/* Organization List */}
                  <div
                    ref={listRef}
                    className="max-h-72 overflow-y-auto overflow-x-hidden px-4 pb-5 scroll-smooth overscroll-contain
                      [scrollbar-width:thin]
                      [&::-webkit-scrollbar]:w-2
                      [&::-webkit-scrollbar-track]:bg-slate-100/50
                      [&::-webkit-scrollbar-thumb]:bg-slate-300
                      [&::-webkit-scrollbar-thumb]:rounded-full
                      [&::-webkit-scrollbar-thumb:hover]:bg-slate-400"
                  >
                    {(isSearching || isLoadingRecent) && (
                      <div className="py-12 text-center">
                        <div className="inline-flex items-center gap-2.5 text-sm text-slate-500">
                          <svg className="animate-spin h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {isLoadingRecent ? 'Loading recent...' : 'Searching...'}
                        </div>
                      </div>
                    )}

                    {!isSearching && !isLoadingRecent && searchQuery.length >= 2 && filteredOrgs.length === 0 && (
                      <div className="py-12 text-center">
                        <BuildingOffice2Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm font-medium text-slate-600">No organizations found</p>
                        <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                      </div>
                    )}

                    {!isSearching && !isLoadingRecent && filteredOrgs.length > 0 && (
                      <>
                        <div className="px-1 pb-2">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                            {searchQuery.length >= 2 ? 'Search Results' : 'Recent Organizations'}
                          </p>
                        </div>
                        <div className="space-y-1">
                          {filteredOrgs.map((org, index) => (
                            <button
                              key={org.id}
                              data-org-index={index}
                              onClick={() => switchToOrg(org)}
                              onMouseEnter={() => setHighlightedIndex(index)}
                              className={`w-full px-4 py-3 rounded-xl flex items-center gap-4 transition-all duration-150 text-left group ${
                                highlightedIndex === index
                                  ? 'bg-slate-100 ring-1 ring-slate-200/60'
                                  : 'hover:bg-slate-50/80'
                              }`}
                            >
                              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getOrgGradient(org.name)} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow`}>
                                <span className="text-sm font-bold text-white">{getInitials(org.name)}</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium break-words ${
                                  highlightedIndex === index ? 'text-slate-900' : 'text-slate-800'
                                }`}>
                                  {org.name}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">Click to switch</p>
                              </div>
                              <ArrowRightOnRectangleIcon className={`w-5 h-5 flex-shrink-0 transition-all ${
                                highlightedIndex === index
                                  ? 'text-indigo-500 opacity-100 translate-x-0'
                                  : 'text-slate-300 opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0'
                              }`} />
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {!isSearching && !isLoadingRecent && searchQuery.length < 2 && filteredOrgs.length === 0 && (
                      <div className="py-12 text-center">
                        <MagnifyingGlassIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm font-medium text-slate-600">Type to search organizations</p>
                        <p className="text-xs text-slate-400 mt-1">Enter at least 2 characters</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3.5 bg-slate-50/80 border-t border-slate-100">
                    <p className="text-xs text-slate-500 text-center">
                      {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default OrgSwitcher;
